"use client";

import { Canvas, useFrame, useThree } from "@react-three/fiber";
import React, { MouseEvent, useEffect, useRef, useState } from "react";
import { Vector3 } from "three";
import styles from "./3d-logo.module.scss";
import classNames from "classnames";
import { useUIStore } from "@/stores/ui-states.store";
import LogoModel from "@/components/blocks/landing-block/3d-logo/3d-logo";
import { useMouseMovement } from "@/hooks/useMouseMovement.hook";

const CanvasRenderer = () => {
  const {
    isModelLoaded,
    setIntroAnimationCompleted,
    introAnimationCompleted,
    setIsMouseOver,
  } = useUIStore();
  const [multiplier, setMultiplier] = useState(1);
  const canvasContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (
      isModelLoaded ||
      window.innerWidth <= parseInt(styles.breakpointSmall)
    ) {
      setTimeout(() => {
        canvasContainerRef.current!.classList.add(styles.fadein);
        setIntroAnimationCompleted(true);
      }, 600);
    }
  }, [canvasContainerRef, isModelLoaded, setIntroAnimationCompleted]);

  //! Change the canvas width along with the scale of the logomodel
  //! The result expected is a typical resize of a css component avoiding the issue with threejs resizing

  useEffect(() => {
    function getMultiplierValue() {
      const width = window.innerWidth;
      if (width < parseInt(styles.breakpointMedium)) {
        return 0.7;
      } else if (width < parseInt(styles.breakpointSmall)) {
        return 0.5;
      } else {
        return 1;
      }
    }

    setMultiplier(getMultiplierValue());

    function handleResize() {
      setMultiplier(getMultiplierValue());
    }

    window.addEventListener("resize", handleResize);

    // Clean up the event listener on component unmount
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div
      ref={canvasContainerRef}
      className={classNames(styles.canvasContainer)}
      style={{ width: 450 * multiplier * 0.9, height: 450 }}
    >
      <Canvas
        onMouseOver={() => setIsMouseOver(true)}
        onMouseOut={() => setIsMouseOver(false)}
      >
        <ambientLight intensity={1.2} />
        <pointLight position={[0, -5, 10]} intensity={100} />
        <pointLight position={[0, 5, 10]} intensity={100} />
        <LogoModel multiplier={multiplier} />
        <Rig />
      </Canvas>
    </div>
  );
};

function Rig() {
  const LINEAR_INTERP_SPEED: number = 0.01;
  const { camera } = useThree();
  const { mouseX, mouseY } = useMouseMovement();
  const vec = new Vector3();
  return useFrame(() => {
    camera.position.lerp(
      vec.set(-mouseX * 1.2, mouseY * 1.2, camera.position.z),
      LINEAR_INTERP_SPEED,
    );
    camera.lookAt(0, 0, 0);
  });
}

export default CanvasRenderer;
